import React from 'react';
import { IconCopy, IconDelete, IconAddPerson } from '@bynder/icons';
import { Tabs, notify } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { usePageMutations } from '../../mutations/pages';
import { ModalType } from '../../types';
import { useModuleTier } from '../../helpers/hooks';
import { LinkCopiedToClipboardNotification } from '../../types/notifications';
import { AbilityContext } from '../../context/ability';
import { useCurrentUserIdentity } from '../../queries/identity';
import { GUIDELINES_PERMISSIONS } from '../../helpers/permissions';
import { GuideAbility } from '../../helpers/abilities';
import { copyShareUrlToClipboard } from '../../constants/widgets';
import { useGuideStore } from '../../stores/guideStore';
import { useModalStore } from '../../stores/modalStore';

export const PageInspectorFooter: React.FC<{
  guideOwnerId: string;
  pageOwnerId: string;
  pageId: string;
  guideId: string;
  pageTitle: string;
  canEditGuide: boolean;
}> = ({
  guideOwnerId,
  pageOwnerId,
  pageId,
  guideId,
  pageTitle,
  canEditGuide,
}) => {
  const openModal = useModalStore.use.openModal();
  const { translate } = useTranslate();
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const activeChapterId = useGuideStore(s => s.activeChapterId);
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion);
  const abilities = React.useContext(AbilityContext);

  const { deletePage } = usePageMutations();
  const { isBasic } = useModuleTier();
  const { data: currentUser } = useCurrentUserIdentity();

  const isPageOwner = currentUser?.id === pageOwnerId;
  const isGuideOwnerOrManage = abilities.can(
    GUIDELINES_PERMISSIONS.CREATE,
    new GuideAbility(guideOwnerId),
  );
  const canUsePageAccess = (isGuideOwnerOrManage || isPageOwner) && !isBasic;

  const onDeletePage = () => deletePage.mutate({ pageId });

  const handleShareClick = () => {
    copyShareUrlToClipboard(guideId, pageId)
      .then(() => notify(LinkCopiedToClipboardNotification))
      .catch(e => {
        throw new Error(e);
      });
  };

  const toggleDeletePageModal = () => {
    const modalProps = {
      headerText: translate('DELETE_PAGE_MODAL.HEADER_TEXT'),
      contentText: translate('DELETE_PAGE_MODAL.CONTENT_TEXT', {
        title: pageTitle,
      }),
      checkbox1Text: translate('DELETE_GUIDE_MODAL.CHECKBOX_1_TEXT'),
      checkbox2Text: translate('DELETE_GUIDE_MODAL.CHECKBOX_2_TEXT'),
      buttonText: translate('DELETE_PAGE_MODAL.BUTTON_TEXT'),
    };
    openModal({
      modalType: ModalType.DELETE_MODAL,
      modalOnSubmit: onDeletePage,
      modalProps,
    });
  };

  const toggleAccessModal = () => {
    const modalProps = {
      entityId: activePageId,
      activeGuideId,
      headerText: translate('ACCESS_MODAL.HEADER_TEXT', {
        title: pageTitle,
      }),
      onCopyUrl: handleShareClick,
    };
    openModal({
      modalType: ModalType.ACCESS_MODAL,
      modalOnSubmit: () => null,
      modalProps,
    });
  };

  const toggleMovePageModal = () => {
    const modalProps = {
      pageTitle: pageTitle,
      activeGuideId,
      chapterId: activeChapterId,
      pageId: activePageId,
      activeGuideVersion,
    };
    openModal({
      modalType: ModalType.MOVE_PAGE_MODAL,
      modalOnSubmit: () => null,
      modalProps,
    });
  };

  return (
    <Tabs variant="clean">
      {canEditGuide && (
        <Tabs.Tab
          data-testid="pageDuplicateButton"
          icon={<IconCopy />}
          isLabelHidden
          label="Duplicate"
          onClick={toggleMovePageModal}
        />
      )}
      {canUsePageAccess && (
        <Tabs.Tab
          data-testid="pageShareButton"
          icon={<IconAddPerson />}
          isLabelHidden
          label="Share"
          onClick={toggleAccessModal}
        />
      )}
      {(isGuideOwnerOrManage || isPageOwner) && (
        <Tabs.Tab
          data-testid="pageDeleteButton"
          icon={<IconDelete />}
          isLabelHidden
          label="Delete"
          onClick={toggleDeletePageModal}
        />
      )}
    </Tabs>
  );
};
